"use client"

import { useRouter } from "next/navigation"
import { useCallback, useMemo } from "react"
import { useCurrentLocale } from "@/shared/hooks/use-current-locale"
import { localizePath } from "@/shared/lib/i18n/config"

type NavigationOptions = {
  scroll?: boolean
}

function localizeHref(href: string, locale: ReturnType<typeof useCurrentLocale>): string {
  if (!href.startsWith("/") || href.startsWith("//")) return href
  return localizePath(href, locale)
}

export function useLocalizedRouter() {
  const router = useRouter()
  const locale = useCurrentLocale()

  const localize = useCallback(
    (href: string) => localizeHref(href, locale),
    [locale],
  )

  const push = useCallback(
    (href: string, options?: NavigationOptions) => router.push(localize(href), options),
    [localize, router],
  )

  const replace = useCallback(
    (href: string, options?: NavigationOptions) => router.replace(localize(href), options),
    [localize, router],
  )

  const prefetch = useCallback(
    (href: string) => router.prefetch(localize(href)),
    [localize, router],
  )

  return useMemo(
    () => ({
      ...router,
      localize,
      push,
      replace,
      prefetch,
    }),
    [localize, prefetch, push, replace, router]
  )
}
